import React, {
  forwardRef,
  HTMLAttributes,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { Ref } from 'react'

import { PijmaComponent } from '@pijma/helper-component'
import { InputProps, InputState, InputStylesStructure } from '@pijma/hero-types'
import { FocusContext } from '@pijma/react'
import { useStyles } from '@pijma/theme-provider'

import { InputBaseStyles } from './Input.style'
import { createEvent } from './utils/createEvent'

export const Input = forwardRef(
  // eslint-disable-next-line sonarjs/cognitive-complexity
  (props: InputProps, ref: Ref<HTMLDivElement>) => {
    const {
      label,
      errorMessage,
      allowClear,
      placeholder,
      name,
      value,
      description,
      onChange,
      type = 'text',
      disabled,
      maxLength,
      onFocus,
      onBlur,
      startIcon,
      descriptionLink,
      endIcon,
      tabIndex,
      onKeyDown,
      autoFocus,
      readOnly,
      onPaste,
      testId,
      inputMode = props.inputMode as HTMLAttributes<
        HTMLInputElement | HTMLTextAreaElement
      >['inputMode'],
    } = props
    const inputRef = useRef<HTMLInputElement>(null)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const nativeFieldRef = props.textarea ? textareaRef : inputRef
    const { registerInput } = useContext(FocusContext)
    const [hover, setHover] = useState(false)
    const [focus, setFocus] = useState(false)
    const [visibility, setVisibility] = useState(false)

    const id = useId()
    const state = useMemo<InputState>(
      () => ({
        isFocused: focus,
        isHover: hover,
        passwordVisibility: visibility,
      }),
      [hover, focus, visibility],
    )

    useEffect(() => {
      if (registerInput && name) {
        registerInput(name, nativeFieldRef)
      }
    }, [name, registerInput, nativeFieldRef])

    const {
      classNames: className,
      components: {
        StartIcon,
        EndIcon,
        ClearIcon,
        DescriptionLink,
        PasswordIcon,
      },
    } = useStyles<InputStylesStructure>(
      ['Input'],
      { ...props, ...state },
      {
        defaultStyles: InputBaseStyles,
      },
    )

    const clearHandler = () => {
      if (nativeFieldRef.current && onChange) {
        const customEvent = createEvent(nativeFieldRef, '')
        customEvent && onChange(customEvent)
        nativeFieldRef.current?.focus()
      }
    }

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocus(true)
      if (onFocus) {
        onFocus(e)
      }
    }
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setFocus(false)
      if (onBlur) {
        onBlur(e)
      }
    }

    const handleChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      if (onChange) {
        onChange(e)
      }
    }

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (onPaste) {
        onPaste(e)
      }
    }

    const keyDownHandler = (
      e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      if (onKeyDown) {
        onKeyDown(e)
      }
    }

    const switchPasswordVisibility = () => {
      setVisibility((prev) => !prev)
    }

    const inputProps = {
      tabIndex,
      value,
      autoFocus,
      onChange: handleChange,
      placeholder,
      disabled,
      ref: inputRef,
      id,
      name,
      type,
      className: className.input,
      maxLength,
      onKeyDown: keyDownHandler,
      readOnly,
      'data-testid': testId ? `${testId}.input` : undefined,
    }
    const textareaProps = {
      ...inputProps,
      ref: textareaRef,
      className: className.textarea,
      'data-testid': testId ? `${testId}.textarea` : undefined,
    }
    return (
      <PijmaComponent
        as="div"
        commonProps={props}
        className={className.root}
        ref={ref}
      >
        <div
          className={className.childrenWrapper}
          onClick={() => nativeFieldRef.current?.focus()}
          onMouseEnter={() => {
            setHover(true)
          }}
          onMouseLeave={() => {
            setHover(false)
          }}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onPaste={handlePaste}
        >
          {startIcon && <div className={className.startIcon}>{StartIcon}</div>}
          <label htmlFor={id} className={className.label}>
            {label}
          </label>
          {props.textarea ? (
            <textarea {...textareaProps} />
          ) : (
            <input
              {...inputProps}
              inputMode={inputMode === 'decimal' ? inputMode : 'text'}
              type={visibility ? 'text' : props.type}
            />
          )}
          {allowClear && (
            <div
              role="button"
              className={className.clearIcon}
              onClick={clearHandler}
            >
              {ClearIcon}
            </div>
          )}
          {props.withPasswordToggle && (
            <div
              className={className.endIcon}
              onClick={switchPasswordVisibility}
            >
              {PasswordIcon}
            </div>
          )}
          {endIcon && <div className={className.endIcon}>{EndIcon}</div>}
        </div>
        <div className={className.descriptionWrapper}>
          {description && (
            <div className={className.description}>{description}</div>
          )}
          {errorMessage && (
            <div className={className.error}>{errorMessage}</div>
          )}
          {descriptionLink && (
            <div className={className.descriptionLink}>{DescriptionLink}</div>
          )}
        </div>
      </PijmaComponent>
    )
  },
)

Input.displayName = 'Input'
